export interface TextValidationOptions {
  maxCharacters?: number;
  removeNewLines?: boolean;
}

/**
 * Returns text after decoding HTML entities and stripping tags.
 */
export function getDecodedText(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent ?? '';
}

/**
 * Returns full string length: ALL characters count - tags, entities, text.
 * <b> = 3 chars, &nbsp; = 6 chars. Matches server-side validation.
 */
export function getRawContentLength(
  html: string,
  removeNewLines = false
): number {
  let content = html;
  if (removeNewLines) {
    content = content.replace(/\r?\n|\r/g, '');
  }
  return content.length;
}

/**
 * Removes incomplete HTML tag at the end (e.g. "<b" or "</b").
 */
function trimIncompleteTag(str: string): string {
  return str.replace(/<[^>]*$/, '');
}

/**
 * Removes incomplete HTML entity at the end (e.g. "&nb" -> "").
 */
function trimIncompleteEntity(str: string): string {
  return str.replace(/&[a-zA-Z0-9#]*$/, '');
}

/**
 * Sanitizes HTML by full length limit. Truncates at 512 chars, fixes broken tags/entities.
 * Returns decoded text for textContent.
 */
export function sanitizeRawHtmlContent(
  html: string,
  options: TextValidationOptions
): string {
  let content = html;

  if (options.removeNewLines) {
    content = content.replace(/\r?\n|\r/g, '');
  }

  const maxCharacters = options?.maxCharacters as number;

  if (maxCharacters >= 0 && content.length > maxCharacters) {
    content = content.slice(0, maxCharacters);
    content = trimIncompleteTag(content);
    content = trimIncompleteEntity(content);
  }

  const div = document.createElement('div');
  div.innerHTML = content;
  return div.textContent ?? '';
}

export function sanitizeTextContent(
  text: string,
  options: TextValidationOptions
): string {
  let sanitized = text;

  if (options.removeNewLines) {
    sanitized = sanitized.replace(/\r?\n|\r/g, '');
  }

  const maxCharacters = options?.maxCharacters as number;

  if (maxCharacters >= 0) {
    if (sanitized.length > maxCharacters) {
      sanitized = sanitized.slice(0, maxCharacters);
    }
  }

  return sanitized;
}
